import { FormControl, FormLabel } from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { dataAttr, FocusableElement } from "@chakra-ui/utils";
import React, { FunctionComponent, useMemo } from "react";
import { useController } from "react-hook-form";
import { SelectInstance } from "react-select";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { InputMessages } from "shared/components/form/InputContent";
import {
  SelectBaseProps,
  SelectInternal,
} from "shared/components/input/select/SelectInternal";
import { SelectElement } from "shared/components/input/select/SelectOption";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import {
  getSelectOptionsFromChildren,
  isSelectOptionData,
} from "shared/components/input/select/SelectUtils";
import { FormInputValue } from "shared/types/FormInputValue";
import { getFieldError } from "shared/utils/FormUtils";

export interface SelectProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> extends SelectBaseProps<T, IsMulti> {
  title: string;
  /***
   @deprecated Use SelectOption in children
   ***/
  elements?: SelectElement[];
  children?: React.ReactNode;
  error?: string;
  helpText?: string;
  hideMessages?: boolean;
  controlSx?: SystemStyleObject;
  isReadOnly?: boolean;
}

// This type is needed so the generic type args are not lost when using forwardRef
type Select = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  props: SelectProps<T, IsMulti> & {
    ref?: React.Ref<
      SelectInstance<SelectOptionData, IsMulti> | FocusableElement
    >;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const Select = React.forwardRef(
  <T extends FormInputValue = string, IsMulti extends boolean = false>(
    {
      elements,
      title,
      hideMessages,
      helpText,
      controlSx,
      createObjectName = title,
      children,
      error,
      ...props
    }: SelectProps<T, IsMulti>,
    ref: React.Ref<SelectInstance<SelectOptionData, IsMulti>>,
  ): React.ReactElement | null => {
    const optionsFromChildren = useMemo(
      () => getSelectOptionsFromChildren(children),
      [children],
    );

    const optionsFromElements = useMemo(
      () =>
        elements?.map(
          ({ value, rich_value }): SelectOptionData => ({
            label: rich_value,
            value,
          }),
        ),
      [elements],
    );

    const options = optionsFromElements ?? optionsFromChildren;

    const hasEmptyOption = useMemo(
      () => options.some((it) => isSelectOptionData(it) && !it.value),
      [options],
    );

    const { isDisabled, isReadOnly, value } = props;

    // TODO ENG-8089: consistent spacing
    return (
      <FormControl
        isInvalid={!!error}
        isDisabled={isDisabled}
        data-invalid={dataAttr(!!error)}
        data-disabled={dataAttr(isDisabled)}
        data-indeterminate={dataAttr(
          Array.isArray(value) ? !value.length : !hasEmptyOption && !value,
        )}
        aria-readonly={dataAttr(isReadOnly)}
        sx={{
          ...controlSx,
          ".react-select__control": {
            bg: "white",
          },
        }}
      >
        <FormLabel variant="floating">{title}</FormLabel>
        <SelectInternal<T, IsMulti>
          ref={ref}
          createObjectName={createObjectName}
          options={options}
          placeholder=""
          {...props}
        />
        {hideMessages || <InputMessages helpText={helpText} error={error} />}
      </FormControl>
    );
  },
) as Select;
(Select as FunctionComponent).displayName = "Select";

export type FormSelectProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> = Omit<SelectProps<T, IsMulti>, "name" | "value" | "error"> & {
  name: string;
  valueAsNumber?: boolean;
  defaultValue?: string;
  unsetWhenEmpty?: boolean;
  shouldUnregister?: boolean;
};

type FormSelect = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  props: FormSelectProps<T, IsMulti> & {
    ref?: React.Ref<
      SelectInstance<SelectOptionData, IsMulti> | FocusableElement
    >;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const FormSelect = React.forwardRef<
  SelectInstance<SelectOptionData> | FocusableElement,
  FormSelectProps
>(
  (
    {
      name,
      elements,
      valueAsNumber,
      defaultValue = "",
      unsetWhenEmpty = false,
      isDisabled,
      children,
      shouldUnregister = true,
      onChangeValue,
      ...otherProps
    },
    ref,
  ) => {
    valueAsNumber = valueAsNumber ?? typeof elements?.[0]?.value === "number";
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { value, onChange, ...field },
      formState: { errors },
    } = useController<{
      select?: FormInputValue;
    }>({
      name: prefixedName as "select",
      shouldUnregister,
      defaultValue: defaultValue || (unsetWhenEmpty ? undefined : ""),
    });
    const isFormDisabled = useIsFormDisabled();
    return (
      <Select
        {...field}
        {...otherProps}
        ref={ref}
        elements={elements?.map((it) => ({
          rich_value: it.rich_value,
          value: valueAsNumber ? it.value.toString() : it.value,
        }))}
        isDisabled={isDisabled || isFormDisabled}
        error={getFieldError(errors, prefixedName)?.message}
        value={
          (valueAsNumber ? value?.toString() : (value as string)) ??
          defaultValue
        }
        // FIXME: `value` type should reflect if unsetWhenEmpty
        //  or valueAsNumber is set, but this makes the types very complicated.
        onChangeValue={(value) => {
          if (!value) {
            onChange(unsetWhenEmpty ? undefined : "");
          } else {
            onChange(valueAsNumber ? Number(value) : value);
          }
          onChangeValue?.(value);
        }}
      >
        {children}
      </Select>
    );
  },
) as FormSelect;
(FormSelect as FunctionComponent).displayName = "FormSelect";
